import {Form, Input, InputNumber, Modal} from "antd";
import {FormItem} from "react-hook-form-antd";
import {useForm} from "react-hook-form";
import {
  INewUserIngredientInput,
  IUpdateUserIngredientInput,
  IUserIngredientFieldsFragment,
  useCreateUserIngredientMutation,
  useUpdateUserIngredientMutation
} from "app/gql/graphqlSchema";
import {MaybeNil} from "app/util/types";
import {isNil} from "app/util/stdlib";
import {NotificationInstance} from "antd/es/notification/interface";

export type IUserIngredientModalData = MaybeNil<{
  userIngredient?: MaybeNil<IUserIngredientFieldsFragment>;
  defaults?: MaybeNil<Partial<Omit<IUserIngredientFieldsFragment, "id">>>;
}>;

interface IProps {
  isModalOpen: boolean;
  onClose: () => void;
  data: IUserIngredientModalData;
  notify?: NotificationInstance;
}

export const UserIngredientModal = ({isModalOpen, onClose, data, notify}: IProps) => {
  const [createUserIngredient] = useCreateUserIngredientMutation();
  const [updateUserIngredient] = useUpdateUserIngredientMutation();

  const handleCancel = () => {
    onClose();
  };

  const handleOk = async () => {
    await saveUserIngredient();
  };

  const {userIngredient} = data ?? {};
  const {defaults} = data ?? {};

  const isEdit = !isNil(userIngredient);

  const nutrition = userIngredient?.nutrition;
  const defaultsNutrition = defaults?.nutrition;

  const {control, formState, handleSubmit} = useForm({
    values: {
      name: userIngredient?.nameEn ?? defaults?.nameEn ?? "",
      carb: nutrition?.carb ?? defaultsNutrition?.carb ?? "",
      energyKcal: nutrition?.energyKcal ?? defaultsNutrition?.energyKcal ?? "",
      energyKj: nutrition?.energyKj ?? defaultsNutrition?.energyKj ?? "",
      fatSaturated: nutrition?.fatSaturated ?? defaultsNutrition?.fatSaturated ?? "",
      fatTotal: nutrition?.fatTotal ?? defaultsNutrition?.fatTotal ?? "",
      protein: nutrition?.protein ?? defaultsNutrition?.protein ?? "",
      salt: nutrition?.salt ?? defaultsNutrition?.salt ?? "",
      sugar: nutrition?.sugar ?? defaultsNutrition?.sugar ?? ""
    }
  });

  const saveUserIngredient = handleSubmit(async (values) => {
    const input: INewUserIngredientInput | IUpdateUserIngredientInput = {
      nameEn: values.name,
      descriptionEn: "",
      scientificName: "",
      carb: values.carb,
      energyKcal: values.energyKcal,
      energyKj: values.energyKj,
      fatSaturated: values.fatSaturated,
      fatTotal: values.fatTotal,
      protein: values.protein,
      salt: values.salt,
      sugar: values.sugar
    };

    if (isEdit) {
      await updateUserIngredient({
        variables: {
          input: {
            ...input,
            id: userIngredient.id
          }
        }
      });
      notify?.success({message: `Successfully updated ${values.name}`});
    } else {
      await createUserIngredient({
        variables: {
          input
        }
      });
      notify?.success({message: `Successfully created ${values.name}`});
    }
    onClose();
  });

  const modalTitle = isEdit ? `Edit Ingredient: ${userIngredient.nameEn}` : "Create Ingredient";

  return (
    <Modal
      centered
      destroyOnClose
      okButtonProps={{htmlType: "submit", disabled: !formState.isValid || !formState.isDirty}}
      okText="Save"
      onCancel={handleCancel}
      onOk={handleOk}
      open={isModalOpen}
      title={modalTitle}
    >
      <div className="my-8">
        <Form labelCol={{span: 6}} wrapperCol={{span: 18}}>
          <FormItem control={control} label="Name" name="name">
            <Input />
          </FormItem>
          <FormItem control={control} label="Energy (kcal)" name="energyKcal">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Energy (kJ)" name="energyKj">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Fat (total)" name="fatTotal">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Fat (saturated)" name="fatSaturated">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Carbohydrates" name="carb">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Sugar" name="sugar">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Protein" name="protein">
            <InputNumber changeOnWheel />
          </FormItem>
          <FormItem control={control} label="Salt" name="salt">
            <InputNumber changeOnWheel />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
};
